/**
 * EditAccommodationModal Component
 *
 * Edit modal for Visbook accommodation products only (productType === 'accommodation').
 * Note: 'nights' products are regular Bilberry products and use EditActivityModal.
 */

import { type JSX, useMemo } from 'react';
import { CartItem } from '@repo/types';
import { useLocale } from '@repo/i18n';
import { capitalize } from '@repo/common-utils/TextUtils';
import { getCartItemDisplayTitle } from '@repo/widget-utils/display-helper';
import { getDateRangeVariant } from 'src/widgets/activities/activity-booking/booking/Booking';
import { useBookingContext } from 'src/widgets/BookingContext';
import useSummaryPriceLines from 'src/hooks/domain/useSummaryPriceLines';
import { usePricePropsFromQuantitiesInBookingContext } from '../../booking-widget-card/booking-header/BookingHeader';
import BookingBase from '../../booking-base/BookingBase';
import { EditModalWrapper } from './primitives/EditModalWrapper';
import { useEditCartItem } from './primitives/useEditCartItem';
import { useEditModalState } from './primitives/useEditModalState';
import { useActivityQuantities } from 'src/hooks/domain/booking/useActivityQuantities';
import { showError } from 'src/utils/widget/error-handling';
import { mergeAccommodationSummaryLines } from '../accommodation-summary';

export type EditAccommodationModalProps = {
    cartItem: CartItem;
    anchorEl: HTMLElement | null;
    onClose: () => void;
};

/**
 * Inner content component - uses BookingContext provided by EditModalWrapper
 */
function EditAccommodationModalContent(props: EditAccommodationModalProps): JSX.Element {
    const { cartItem, anchorEl, onClose } = props;
    const { t } = useLocale();
    const { hasChosenDate, isLoadingPrices, selectedTicketType, quantities } = useBookingContext();

    // 🟢 Reuse: Modal state management
    const modalState = useEditModalState({ cartItem, anchorEl });

    // 🟢 Reuse: Save/cancel logic
    const { save, cancel } = useEditCartItem({
        cartItem,
        onAfterSave: onClose,
    });

    const { inputItems, errors, onChangeQuantity } = useActivityQuantities();

    // Derive UI data
    const productTitle = getCartItemDisplayTitle(cartItem);
    const numPrices = modalState.product?.ticketOptions.length;

    const dateRangeVariant = getDateRangeVariant(
        modalState.product ?? cartItem.products[0]?.product,
    );

    // Price summary. For accommodation Visbook returns the booking total per
    // ticketType, so each ticket option with quantity > 0 would otherwise render
    // its own summary line at that full total — duplicating the price. Collapse
    // priceQuantities to a single entry and override the line label below to
    // surface the Voksne / Barn breakdown (same pattern as Booking.tsx).
    const quantitiesForSummary = hasChosenDate
        ? modalState.priceQuantities.slice(0, 1).map((q) => ({ ...q, quantity: 1 }))
        : [];
    const rawSummaryLines = useSummaryPriceLines(
        quantitiesForSummary,
        false, // isDetailedLines
        isLoadingPrices,
        selectedTicketType,
    );
    const summaryLines = useMemo(
        () => mergeAccommodationSummaryLines(rawSummaryLines, quantities, t),
        [rawSummaryLines, quantities, t],
    );

    // Booking header price props
    const bookingHeaderPriceProps = usePricePropsFromQuantitiesInBookingContext(
        modalState.priceQuantities,
        undefined, // fromPrice
    );

    // Translate errors to warning label
    const warningLabel =
        errors.length > 0
            ? errors[0].error === 'minEntrants'
                ? t.a_minimum_of_participants_is_required_to_book_this_product
                : t.no_available_capacity_for_this_tour
            : undefined;

    // Error handling wrapper for save operation
    const handleSave = async () => {
        try {
            await save();
        } catch (_error) {
            showError(t.an_error_occurred_when_submitting_your_request);
        }
    };

    return (
        <BookingBase
            disableFixedPosition={true}
            productTitle={productTitle}
            numPrices={numPrices}
            travelerQuantities={modalState.priceQuantities}
            availabilityData={modalState.availabilities}
            leftButtonLabel={capitalize(t.cancel)}
            rightButtonLabel={capitalize(t.update)}
            onClickLeftButton={cancel}
            onClickRightButton={handleSave}
            availabilitySearchPeriod={modalState.availabilitySearchPeriod}
            setAvailabilitySearchPeriod={modalState.setAvailabilitySearchPeriod}
            productCapacity={modalState.productCapacity}
            title={t.edit}
            addMarginRightToHeader={true}
            expandArrowInside={false}
            positionOffscreen={false}
            selectedDateRange={modalState.dateRange}
            onSelectDateRange={modalState.setDateRange}
            ticketTypes={modalState.ticketTypes}
            dateRangeVariant={dateRangeVariant}
            minDate={modalState.minBookingDate}
            isLoadingAvailabilities={modalState.isLoadingAvailabilities}
            summaryLines={summaryLines}
            quantityErrors={errors}
            totalErrors={errors}
            disableBookButton={errors.length > 0}
            warningLabel={warningLabel}
            quantityInputItems={inputItems}
            onChangeQuantity={onChangeQuantity}
            bookingHeaderPriceProps={bookingHeaderPriceProps}
        />
    );
}

/**
 * Accommodation edit modal - provides BookingContext via EditModalWrapper
 *
 * Note: Accommodation products use ticket categories like activities,
 * but have different business rules (nights, rooms, etc.).
 * For now, we use the same quantity system as activities.
 *
 * Future: May need dedicated useAccommodationQuantities hook.
 */
export function EditAccommodationModal(props: EditAccommodationModalProps): JSX.Element {
    const { cartItem, anchorEl, onClose } = props;

    return (
        <EditModalWrapper cartItem={cartItem} anchorEl={anchorEl} onCancel={onClose}>
            <EditAccommodationModalContent {...props} />
        </EditModalWrapper>
    );
}
